import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * #2500 / #2505 / #2495: the collapsed launcher gets out of a control's way.
 *
 * The launcher is `fixed` bottom-right over every page (z-40, D-2398). `<main>`
 * carries `pb-24` so the last row can scroll clear of it (#2278), but a control
 * mid-page still passes under it: the committee panel's Save at 390 px, a
 * /bills row's trash icon, /renew's "Save & continue" before the end of the
 * page. Padding cannot fix those, so the launcher looks at what is under its
 * home position. When a button, link or field is there, it slides right until
 * only a sliver shows. The sliver sits inside the page's 16 px side gutter, so
 * it covers nothing, and it still opens the panel. It slides back as soon as
 * the control has scrolled away.
 */

/**
 * How far the tucked launcher slides right. Its right edge sits 20 px in
 * (`right-5`) and it is 52 px wide, so 58 px leaves a 14 px sliver: narrower
 * than the 16 px phone gutter (`px-4` on `<main>`).
 */
export const LAUNCHER_TUCK_PX = 58;

/** The classes for the tucked state. Focus brings the whole button back. */
export const LAUNCHER_TUCK_CLASS = 'translate-x-[58px] focus-visible:translate-x-0';

const INTERACTIVE =
  'button, a[href], input, select, textarea, label, summary, [role="button"], [role="link"], [role="checkbox"], [role="radio"], [role="switch"], [role="tab"], [role="combobox"], [role="menuitem"], [contenteditable="true"]';

/**
 * True when a page control lies under the launcher's home box.
 *
 * The box comes from `offset*`, the layout position, which a transform does
 * not move. Reading `getBoundingClientRect` instead would sample the tucked
 * position once tucked, find nothing, untuck, and loop.
 */
export function launcherCoversControl(launcher: HTMLElement): boolean {
  if (typeof document.elementsFromPoint !== 'function') return false;
  const { offsetLeft: left, offsetTop: top, offsetWidth: w, offsetHeight: h } = launcher;
  if (!w || !h) return false;
  // The centre and four points on the round button's body.
  const points: Array<[number, number]> = [
    [left + w / 2, top + h / 2],
    [left + w * 0.25, top + h * 0.25],
    [left + w * 0.75, top + h * 0.25],
    [left + w * 0.25, top + h * 0.75],
    [left + w * 0.75, top + h * 0.75],
  ];
  return points.some(([x, y]) => {
    const below = document.elementsFromPoint(x, y).find((el) => el !== launcher && !launcher.contains(el));
    return Boolean(below?.closest(INTERACTIVE));
  });
}

/**
 * Whether the launcher should be tucked right now. Re-checks once per frame on
 * any scroll (captured, so `<main>`'s own scroll counts), on resize, and when
 * the page's DOM changes (a list that loads under a still launcher).
 */
export function useLauncherTuck(launcherRef: RefObject<HTMLElement | null>, active: boolean): boolean {
  const [tucked, setTucked] = useState(false);
  const tuckedRef = useRef(false);

  useEffect(() => {
    if (!active) {
      tuckedRef.current = false;
      setTucked(false);
      return;
    }
    let frame = 0;
    const check = () => {
      frame = 0;
      const el = launcherRef.current;
      if (!el) return;
      const next = launcherCoversControl(el);
      if (next !== tuckedRef.current) {
        tuckedRef.current = next;
        setTucked(next);
      }
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(check);
    };
    schedule();
    document.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', schedule);
    const observer = typeof MutationObserver === 'undefined' ? null : new MutationObserver(schedule);
    observer?.observe(document.body, { childList: true, subtree: true });
    return () => {
      if (frame) cancelAnimationFrame(frame);
      document.removeEventListener('scroll', schedule, { capture: true });
      window.removeEventListener('resize', schedule);
      observer?.disconnect();
    };
  }, [active, launcherRef]);

  return active && tucked;
}
